import React, { useMemo } from "react";
import { formatEstimateStatus } from "../../../helpers/estimates";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { DeskPanel, DeskPill } from "../../desktop/DeskTable";
import { DeskLine } from "../desktop/EstimateSummaryDesktop";
import { estimateStatusTone } from "../desktop/estimateDesktopUi";
import {
  computeEstimateTotals,
  formatCurrency,
} from "../../../helpers/estimateTotals";
import { computeLivePricing } from "../../../helpers/estimateTax";
import { LocationReviewSection } from "./LocationReviewSection";
import {
  DOC_CARD_STYLE,
  DOC_FIELD_LABEL,
  DOC_FIELD_ROW,
  DOC_FIELD_VALUE,
  DOC_LINE_ROW,
  DOC_LINE_ROW_EMPHASIS,
  DOC_SECTION_TITLE,
} from "./estimateReviewStyles";

const SECTION_DIVIDER = {
  marginTop: "28px",
  paddingTop: "20px",
  borderTop: "1px solid rgba(201,168,76,0.2)",
};

function SummaryLine({ label, value, emphasis = false }) {
  const style = emphasis ? DOC_LINE_ROW_EMPHASIS : DOC_LINE_ROW;
  return (
    <div style={style}>
      <span>{label}</span>
      <span>{formatCurrency(value)}</span>
    </div>
  );
}

export function EstimateReviewView({
  estimateTree,
  materialsCatalog,
  clientName,
  estimateNumber,
  projectName,
  status,
}) {
  const isDesktop = useIsDesktopViewport();
  const financials = useMemo(
    () => computeEstimateTotals(estimateTree, materialsCatalog),
    [estimateTree, materialsCatalog],
  );
  const pricedAdjustment = useMemo(() => {
    if (
      estimateTree?.subtotal_adjustment_value == null
      || estimateTree.subtotal_adjustment_value === ""
    ) {
      return null;
    }
    return computeLivePricing({
      estimateSubtotal: financials.calculatedEstimateTotal,
      waste: financials.wasteAdjustment,
      travel: financials.travelAdjustment,
      misc: financials.miscAdjustment,
      adjustmentMode: estimateTree.subtotal_adjustment_mode,
      adjustmentValue: estimateTree.subtotal_adjustment_value,
    });
  }, [estimateTree, financials]);

  const locations = estimateTree?.locations || [];

  if (isDesktop) {
    const hasAdjustments =
      financials.wasteAdjustment > 0 ||
      financials.travelAdjustment > 0 ||
      financials.miscAdjustment > 0;
    return (
      <div className="dp-grid dp-grid--main-side">
        <DeskPanel title="Scope">
          {locations.length === 0 ? (
            <div style={{ opacity: 0.7 }}>No locations in this estimate.</div>
          ) : (
            locations.map((location) => (
              <LocationReviewSection
                key={location.room_id}
                location={location}
                materialsCatalog={materialsCatalog}
              />
            ))
          )}
        </DeskPanel>

        <aside className="est-d-side" aria-label="Estimate review summary">
          <DeskPanel title={projectName || estimateTree?.title || "Untitled Estimate"}>
            <dl className="dp-kv dp-kv--tight">
              <dt>Client</dt>
              <dd>{clientName || "—"}</dd>
              <dt>Name</dt>
              <dd>{estimateNumber || estimateTree?.estimate_number || "—"}</dd>
              <dt>Status</dt>
              <dd>
                <DeskPill tone={estimateStatusTone(status)}>{formatEstimateStatus(status)}</DeskPill>
              </dd>
            </dl>
          </DeskPanel>
          <DeskPanel title="Financial Summary">
            <DeskLine label="Calculated Materials Total" value={financials.calculatedMaterialsTotal} />
            <DeskLine label="Calculated Labour Total" value={financials.calculatedLabourTotal} />
            <DeskLine label="Calculated Estimate Total" value={financials.calculatedEstimateTotal} emphasis />
            {hasAdjustments && (
              <>
                <hr className="est-d-hr" />
                <p className="est-d-subhead">Adjustments</p>
                {financials.wasteAdjustment > 0 && <DeskLine label="Waste Adjustment" value={financials.wasteAdjustment} />}
                {financials.travelAdjustment > 0 && <DeskLine label="Travel Adjustment" value={financials.travelAdjustment} />}
                {financials.miscAdjustment > 0 && <DeskLine label="Misc Adjustment" value={financials.miscAdjustment} />}
                <DeskLine label="Adjusted Total" value={financials.adjustedTotal} emphasis />
              </>
            )}
            {financials.finalEstimatePrice != null && (
              <DeskLine label="Final Estimate Price" value={financials.finalEstimatePrice} emphasis />
            )}
            {pricedAdjustment ? (
              <>
                <DeskLine label="Subtotal adjustment" value={pricedAdjustment.adjustment} />
                <DeskLine label="Subtotal" value={pricedAdjustment.preTax} emphasis />
              </>
            ) : null}
          </DeskPanel>
        </aside>
      </div>
    );
  }

  return (
    <>
      <div style={DOC_CARD_STYLE}>
        <div style={{ fontSize: "20px", fontWeight: 700, marginBottom: "6px" }}>
          {projectName || estimateTree?.title || "Untitled Estimate"}
        </div>
        <div style={{ opacity: 0.75, fontSize: "14px", marginBottom: "16px" }}>
          {estimateNumber || estimateTree?.estimate_number}
        </div>

        <div style={DOC_FIELD_ROW}>
          <span style={DOC_FIELD_LABEL}>Client</span>
          <span style={DOC_FIELD_VALUE}>{clientName || "—"}</span>
        </div>
        <div style={DOC_FIELD_ROW}>
          <span style={DOC_FIELD_LABEL}>Status</span>
          <span style={DOC_FIELD_VALUE}>{formatEstimateStatus(status)}</span>
        </div>
        <div style={DOC_FIELD_ROW}>
          <span style={DOC_FIELD_LABEL}>Estimate Name</span>
          <span style={DOC_FIELD_VALUE}>
            {estimateNumber || estimateTree?.estimate_number || "—"}
          </span>
        </div>
      </div>

      <div style={SECTION_DIVIDER}>
        <div style={DOC_SECTION_TITLE}>Scope</div>
        {locations.length === 0 ? (
          <div style={{ opacity: 0.7 }}>No locations in this estimate.</div>
        ) : (
          locations.map((location) => (
            <LocationReviewSection
              key={location.room_id}
              location={location}
              materialsCatalog={materialsCatalog}
            />
          ))
        )}
      </div>

      <div style={SECTION_DIVIDER}>
        <div style={DOC_SECTION_TITLE}>Financial Summary</div>
        <div style={DOC_CARD_STYLE}>
          <SummaryLine
            label="Calculated Materials Total"
            value={financials.calculatedMaterialsTotal}
          />
          <SummaryLine
            label="Calculated Labour Total"
            value={financials.calculatedLabourTotal}
          />
          <SummaryLine
            label="Calculated Estimate Total"
            value={financials.calculatedEstimateTotal}
            emphasis
          />

          {(financials.wasteAdjustment > 0 ||
            financials.travelAdjustment > 0 ||
            financials.miscAdjustment > 0) && (
            <>
              <div
                style={{
                  marginTop: "14px",
                  marginBottom: "8px",
                  fontWeight: 600,
                  fontSize: "14px",
                  opacity: 0.85,
                }}
              >
                Adjustments
              </div>
              {financials.wasteAdjustment > 0 && (
                <SummaryLine
                  label="Waste Adjustment"
                  value={financials.wasteAdjustment}
                />
              )}
              {financials.travelAdjustment > 0 && (
                <SummaryLine
                  label="Travel Adjustment"
                  value={financials.travelAdjustment}
                />
              )}
              {financials.miscAdjustment > 0 && (
                <SummaryLine
                  label="Misc Adjustment"
                  value={financials.miscAdjustment}
                />
              )}
              <SummaryLine
                label="Adjusted Total"
                value={financials.adjustedTotal}
                emphasis
              />
            </>
          )}

          {financials.finalEstimatePrice != null && (
            <SummaryLine
              label="Final Estimate Price"
              value={financials.finalEstimatePrice}
              emphasis
            />
          )}
          {pricedAdjustment ? (
            <>
              <SummaryLine label="Subtotal adjustment" value={pricedAdjustment.adjustment} />
              <SummaryLine label="Subtotal" value={pricedAdjustment.preTax} emphasis />
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
